import type { WorkflowListing } from "../../../api/content/workflowsApi";
import type { KeptInputNote } from "../../../api/threads/threadDecoders";
import type { ForkPreview, ForkPreviewStep } from "../../../api/threads/threadsApi";
import type { StepNames } from "../data/stepNames";
import type { ThreadBindings } from "../../../api/threads/liveMessages";
import { parseCollectionVersionRef, VERSION_SEPARATOR } from "../../../shared/references/contentReferences";

/**
 * Forking onto other versions (FR-THREAD-2, T-10, D6): the fork keeps its history but may
 * run on the latest model version and on the version of its Workflow in that model, so a
 * change merged since (a prompt, a step) runs in the fork while the original stays as it
 * was. It may also run on another version of its corpus; the server then takes the
 * document at its version there. Which steps the fork keeps is decided by the server
 * (threads.md, As built: fork continuation and reruns).
 */

export interface ForkVersions {
  readonly bindings: ThreadBindings;
  readonly workflow: string | null;
}

/**
 * The bindings and Workflow of a fork onto `latest`: the same document and corpus version,
 * the newest model version, and the Workflow with the same item ID in it. `null` if the
 * Workflow is no longer in the newest model, so the fork cannot follow it. The corpus
 * version is chosen on its own (`otherCorpusVersion`).
 */
export function forkOntoNewest(current: ForkVersions, latest: WorkflowListing): ForkVersions | null {
  const bindings = { ...current.bindings, content_version: latest.version };
  if (current.workflow === null) {
    return { bindings, workflow: null };
  }
  const itemId = itemIdOf(current.workflow);
  const newest = latest.workflows.find((candidate) => itemIdOf(candidate.reference) === itemId);
  return newest === undefined ? null : { bindings, workflow: newest.reference };
}

/** Whether the fork would run on another model or Workflow version than the original does at the fork point. */
export function changesVersions(current: ForkVersions, chosen: ForkVersions): boolean {
  return chosen.workflow !== current.workflow || chosen.bindings.content_version !== current.bindings.content_version;
}

/** The corpus the Thread is bound to, whose versions a fork may move to; `null` without one. */
export function boundCorpusId(current: ForkVersions): string | null {
  const bound = current.bindings.corpus_version;
  return bound === null ? null : (parseCollectionVersionRef(bound)?.collectionId ?? null);
}

/** The corpus version a fork request names: only one other than the Thread's own. */
export function otherCorpusVersion(current: ForkVersions, chosen: string | null): string | undefined {
  return chosen === null || chosen === current.bindings.corpus_version ? undefined : chosen;
}

export type StepOutcome = "kept" | "continues" | "waiting" | "failed" | "later";

/** How a step starts in the previewed fork (D37). */
export function stepOutcome(preview: ForkPreview, step: ForkPreviewStep): StepOutcome {
  if (step.kept) return "kept";
  if (step.stepKey === preview.continueWithStep) return "continues";
  if (step.state === "in_progress") return "waiting";
  if (step.state === "failed") return "failed";
  return "later";
}

/**
 * A previewed step's name as the fork's Workflow names it: its step version's name, else
 * its key's name there; never the key itself (R3-02). `null` when neither is known.
 */
export function previewStepName(names: StepNames, step: ForkPreviewStep): string | null {
  return names.byReference.get(step.step) ?? names.byKey.get(step.stepKey) ?? null;
}

/** The notes the Thread's fork recorded on a step, while it still holds the kept result. */
export function keptNotesOf(notes: readonly KeptInputNote[], stepKey: string, state: string): readonly KeptInputNote[] {
  return state === "done" ? notes.filter((note) => note.stepKey === stepKey) : [];
}

function itemIdOf(reference: string): string {
  const separator = reference.indexOf(VERSION_SEPARATOR);
  return separator === -1 ? reference : reference.slice(0, separator);
}
